import React, { useState, useEffect } from "react";
import Navbar from "../nav_bar/Navbar";
import "./RegisterPage.css";
import { GoogleLogin } from "@react-oauth/google";
import axiosInstance from "../../api/axiosInstance";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { MESSAGES } from "../../../common/messages";
import { TERMS_VERSION } from "../../../common/constants";


const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

const RegisterPage = () => {

  const [step, setStep] = useState("signup");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [isLinking, setIsLinking] = useState(false);
  const [googleId, setGoogleId] = useState(null);

  const [alertMessage, setAlertMessage] = useState("");
  const [showAlertModal, setShowAlertModal] = useState(false);

  const [toastMessage, setToastMessage] = useState("");
  const [showToast, setShowToast] = useState(false);

  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);

  const [isSuspended, setIsSuspended] = useState(false);
  const [suspendReason, setSuspendReason] = useState("");

  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [acceptedGoogleTerms, setAcceptedGoogleTerms] = useState(false);

  const [showGoogleConsent, setShowGoogleConsent] = useState(false);
  const [pendingGoogleData, setPendingGoogleData] = useState(null);
  const [referralCode, setReferralCode] = useState(null);

  const navigate = useNavigate();
  const { login } = useAuth();
  const STATUS_LINK_REQUIRED = "LINK_REQUIRED";

  // ================= REFERRAL CODE CAPTURE =================
useEffect(() => {
  const params = new URLSearchParams(window.location.search);
  const ref = params.get("ref");

  if (ref) {
    console.info("[Referral] Referral code detected:", ref);

if (ref && ref.trim() !== "") {
  const normalizedRef = ref.trim().toUpperCase();

  console.info("[Referral] Referral code detected:", normalizedRef);

  setReferralCode(normalizedRef);
  localStorage.setItem("referralCode", normalizedRef);
} else {
  const storedRef = localStorage.getItem("referralCode");

  if (storedRef) {
    setReferralCode(storedRef.toUpperCase());
  }
}

  } else {
    // Load from storage if exists
    const storedRef = localStorage.getItem("referralCode");
    if (storedRef) {
      setReferralCode(storedRef);
    }
  }
}, []);

  // ================= CHECK EXISTING TOKEN =================
  useEffect(() => {
    const token = localStorage.getItem("token");
    const suspended = localStorage.getItem("isSuspended") === "true";

    if (token && !suspended) {
      console.info("[Auth Redirect] Existing token found. Redirecting home.");
      navigate("/", { replace: true });
    }
  }, [navigate]);

  // ================= RESEND OTP TIMER =================
  useEffect(() => {
    let timer;
    if (resendCooldown > 0) {
      timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
    }
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const showAlert = (message) => {
    console.warn("[Alert]", message);
    setAlertMessage(message);
    setShowAlertModal(true);
  };

  const showToastMessage = (message, duration = 3000) => {
    console.info("[Toast]", message);
    setToastMessage(message);
    setShowToast(true);
    setTimeout(() => setShowToast(false), duration);
  };

  // ================= EMAIL SIGNUP =================
  const handleEmailSignup = async () => {
    console.group("[Email Signup]");
    console.info("Starting email signup flow");

    if (process.env.NODE_ENV !== "production") {
      console.debug("Signup Data:", { name, email, hasPassword: !!password, userTimeZone });
    }

    if (!name || !email || !password) {
      showAlert(MESSAGES.FILL_ALL_FIELDS);
      console.groupEnd();
      return;
    }

    if (!acceptedTerms) {
      showAlert("You must agree to the Terms of Service and Privacy Policy to continue.");
      console.groupEnd();
      return;
    }

    setLoading(true);
    try {
  const res = await axiosInstance.post("/auth_email/signup/request", null, {
  params: {
    name,
    email,
    password,
    userTimeZone,
    referralCode: referralCode || localStorage.getItem("referralCode"),
  },
});
      console.info("Signup request successful. OTP step initiated.");

      setStep("otp");
      setResendCooldown(30);
      showToastMessage("OTP sent to your email");

    } catch (err) {
      console.error("[Email Signup Error]", err.response?.data || err.message);
      showAlert(err.response?.data?.message || MESSAGES.SIGNUP_FAILED);
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  // ================= OTP VERIFY =================
  const handleOtpVerify = async () => {
    console.group("[OTP Verify]");
    console.info("OTP verification started");

    if (process.env.NODE_ENV !== "production") {
      console.debug("OTP Entered:", otp);
    }

    if (!otp) {
      showAlert(MESSAGES.ENTER_OTP);
      console.groupEnd();
      return;
    }

    setLoading(true);
    try {
      if (isLinking) {
        console.info("Account linking OTP verification");

        const res = await axiosInstance.post("/auth/link/confirm", {
          email,
          otp,
          googleId,
          userTimeZone,
        });

        console.info("Account linking successful. Logging in.");
        login(res.data);
        navigate("/", { replace: true });
        return;
      }

  await axiosInstance.post("/auth_email/signup/verify", null, {
  params: {
    email,
    otp,
    userTimeZone,
    referralCode: referralCode || localStorage.getItem("referralCode"),
    termsAccepted: acceptedTerms,
    privacyAccepted: acceptedTerms,
    termsVersion: TERMS_VERSION
  },
});


      console.info("OTP verified. Logging in user.");

      const loginRes = await axiosInstance.post("/auth_email/email_login", null, {
        params: { email, password, userTimeZone },
      });

      login(loginRes.data);
      navigate("/", { replace: true });

    } catch (err) {
      console.error("[OTP Verify Error]", err.response?.data || err.message);
      showAlert(err.response?.data?.message || MESSAGES.OTP_VERIFY_FAILED);
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  // ================= RESEND OTP =================
  const handleResendOtp = async () => {
    console.group("[Resend OTP]");
    console.info("Resend OTP triggered");

    if (process.env.NODE_ENV !== "production") {
      console.debug("Cooldown:", resendCooldown);
    }

    if (resendCooldown > 0) {
      console.groupEnd();
      return;
    }

    setLoading(true);
    try {
      if (isLinking) {
        console.info("Resending OTP for account linking");
        await axiosInstance.post("/auth/link/resend-otp", { email, googleId });
      } else {
        console.info("Resending OTP for signup");
        await axiosInstance.post("/auth_email/signup/resend-otp", null, { params: { email } });
      }

      showToastMessage("OTP has been resent to your email");
      setResendCooldown(30);

    } catch (err) {
      console.error("[Resend OTP Error]", err.response?.data || err.message);
      showToastMessage(err.response?.data?.message || MESSAGES.OTP_RESEND_FAILED);
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  // ================= GOOGLE SIGNUP =================

const handleGoogleSuccess = async (cred) => {
  console.group("[Google Signup]");
  console.info("Google SDK authentication successful");

  if (!cred?.credential) {
    showAlert("Google authentication failed. Please try again.");
    console.groupEnd();
    return;
  }

  setLoading(true);

  try {
    const res = await axiosInstance.post("/auth/google", {
      idToken: cred.credential,
      userTimeZone,
    });

    const backend = res.data?.data ?? res.data;

    console.log("[Google Response]", backend);

    // ================= EXISTING GOOGLE USER =================
    if (backend?.token && backend?.userType === "EXISTING_USER") {
      console.info("Existing Google user. Logging in.");
      login(backend); // new session DTO
      navigate("/", { replace: true });
      return;
    }

    // ================= NEW GOOGLE USER =================
    if (backend?.userType === "NEW_USER") {
      console.info("New Google user. Showing consent.");

      setPendingGoogleData({
        googleId: backend.googleId,
        email: backend.email,
        name: backend.name,
        picture: backend.picture || null,
      });

      setAcceptedGoogleTerms(false);
      setShowGoogleConsent(true);
      return;
    }

  } catch (err) {

    const backend = err.response?.data;

    console.error("[Google Signup Error]", backend);

    // ================= ACCOUNT LINK REQUIRED =================
    if (backend?.status === STATUS_LINK_REQUIRED) {

      console.info("Account linking required.");

      if (!backend.email || !backend.googleId) {
        console.error("Invalid LINK_REQUIRED response:", backend);
        showAlert("Account linking failed. Please retry Google login.");
        return;
      }

      // Save linking data
      const linkingData = {
        email: backend.email,
        googleId: backend.googleId,
        name: backend.name || "",
        picture: backend.picture || null,
      };

      setPendingGoogleData(linkingData);
      setEmail(linkingData.email);
      setGoogleId(linkingData.googleId);

      showAlert("Email exists. Confirm account linking.");
      return;
    }

    showAlert(backend?.message || "Google signup failed");

  } finally {
    setLoading(false);
    console.groupEnd();
  }
};


  // ================= GOOGLE COMPLETE =================
  const handleGoogleCompleteSignup = async () => {
    console.group("[Google Complete Signup]");
    console.info("Completing Google signup after consent");

    if (!pendingGoogleData) {
      console.warn("No pending Google data found.");
      console.groupEnd();
      return;
    }

    if (!acceptedGoogleTerms) {
      showAlert("You must agree to the Terms of Service and Privacy Policy to continue.");
      console.groupEnd();
      return;
    }

    if (process.env.NODE_ENV !== "production") {
      console.debug("Pending Google Data:", pendingGoogleData);
    }

    setLoading(true);

    try {
const res = await axiosInstance.post("/auth/google/complete", {
  googleId: pendingGoogleData.googleId,
  email: pendingGoogleData.email,
  name: pendingGoogleData.name,
  userTimeZone,
  picture: pendingGoogleData.picture,
  referralCode: referralCode || localStorage.getItem("referralCode"),
  termsAccepted: true,
  termsVersion: TERMS_VERSION,
  privacyAccepted: true
});


      console.log("[Google Complete Signup Response]", res.data);

      console.info("Google signup completed successfully.");

     login(res.data);
      setShowGoogleConsent(false);
      setAcceptedGoogleTerms(false);
      navigate("/", { replace: true });

    } catch (err) {
      console.error("[Google Complete Error]", err.response?.data || err.message);
      showAlert(err.response?.data?.message || "Google signup failed");
    } finally {
      setLoading(false);
      console.groupEnd();
    }
  };

  // ================= ACCOUNT LINKING =================
const handleAccountLinking = async () => {
  console.group("[Account Linking]");

  console.log("pendingGoogleData:", pendingGoogleData);

  if (!pendingGoogleData?.email || !pendingGoogleData?.googleId) {
    console.error("Missing linking data:", pendingGoogleData);
    showAlert("Missing account linking data. Please retry Google login.");
    console.groupEnd();
    return;
  }

  setLoading(true);

  try {
    await axiosInstance.post("/auth/google/link-account", {
      email: pendingGoogleData.email,
      googleId: pendingGoogleData.googleId,
    });

    console.info("OTP sent for account linking");

    setShowAlertModal(false);
    setIsLinking(true);
    setStep("otp");

    showToastMessage("OTP sent. Verify to complete linking.", 4000);

  } catch (err) {
    console.error("[Account Linking Error]", err.response?.data);
    showAlert(err.response?.data?.message || "Account linking failed");
  } finally {
    setLoading(false);
    console.groupEnd();
  }
};


  return (
    <div className="login-page">
      <Navbar />
      <div className="login-wrapper">
        <div className="form-container">
          <h2>Create Account</h2>
          <p className="subtitle">
            {MESSAGES.JOIN_TEXT} <b>{MESSAGES.SPEAK_BOLD_WITH_KK_TEXT}</b> {MESSAGES.START_IMPROVING}
          </p>

          <div className="google-wrapper">
            <GoogleLogin
              onSuccess={handleGoogleSuccess}
              onError={() => showAlert(MESSAGES.GOOGLE_SIGNUP_FAILED)}
            />
          </div>

          <div className="or-divider">OR</div>

          {step === "signup" ? (
            <>
              <div className="input-group">
                <input type="text" value={name} onChange={(e) => setName(e.target.value)} placeholder=" " />
                <label>Full Name</label>
              </div>

              <div className="input-group">
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder=" " />
                <label>Email Address</label>
              </div>

              <div className="input-group">
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder=" " />
                <label>Password</label>
              </div>

<div className="input-group" style={{ marginTop: "15px" }}>
  <input
    type="text"
    value={referralCode || ""}
    onChange={(e) => {
      const code = e.target.value.toUpperCase();
      setReferralCode(code);

      if (code) {
        localStorage.setItem("referralCode", code);
      } else {
        localStorage.removeItem("referralCode");
      }
    }}
    placeholder=" "
    maxLength={12}
  />
  <label>Referral Code (Optional)</label>
</div>



              {/* ✅ Terms Checkbox */}
<div className="terms-container">
  <label className="terms-label">
    <input
      type="checkbox"
      checked={acceptedTerms}
      onChange={(e) => setAcceptedTerms(e.target.checked)}
      className="terms-checkbox"
    />
    <span>
      I agree to the{" "}
      <Link to="/terms-and-conditions" target="_blank" className="terms-link">
        Terms of Service
      </Link>{" "}
      and{" "}
      <Link to="/privacy-policy" target="_blank" className="terms-link">
        Privacy Policy
      </Link>.
    </span>
  </label>
</div>


              <button
                className="login-btn"
                onClick={handleEmailSignup}
                disabled={loading || !acceptedTerms}
              >
                {loading ? "Processing..." : "Create Account"}
              </button>
            </>
          ) : (
            <>
              <p className="subtitle">
                Enter the OTP sent to <b>{email}</b>
              </p>

              <div className="input-group">
                <input
                  type="text"
                  value={otp}
                  onChange={(e) => setOtp(e.target.value)}
                  placeholder=" "
                  inputMode="numeric"
                  maxLength={6}
                />
                <label>OTP Code</label>
              </div>

              <button className="login-btn" onClick={handleOtpVerify} disabled={loading}>
                {loading ? "Verifying..." : "Verify & Continue"}
              </button>

              <div style={{ textAlign: "center", marginTop: "12px" }}>
                <button className="btn-secondary" onClick={handleResendOtp} disabled={loading || resendCooldown > 0}>
                  {resendCooldown > 0 ? `Resend OTP in ${resendCooldown}s` : "Resend OTP"}
                </button>
              </div>
            </>
          )}

          <div className="bottom-links">
            Already have an account? <Link to="/login">Login here</Link>
          </div>
        </div>
      </div>

      {showGoogleConsent && (
  <div className="modal-overlay">
    <div className="modal-box">
      <h3>Complete Your Registration</h3>
      <p>
        Welcome <b>{pendingGoogleData?.name}</b>
      </p>
      <p>Please accept our Terms to continue.</p>

      <div className="input-group" style={{ marginTop: "15px" }}>
  <input
    type="text"
    value={referralCode || ""}
    onChange={(e) => {
      const code = e.target.value.toUpperCase();
      setReferralCode(code);

      if (code) {
        localStorage.setItem("referralCode", code);
      } else {
        localStorage.removeItem("referralCode");
      }
    }}
    placeholder=" "
    maxLength={12}
  />
  <label>Referral Code (Optional)</label>
</div>


      <div className="terms-container" style={{ marginTop: "15px" }}>
        <label className="terms-label">
          <input
            type="checkbox"
           checked={acceptedGoogleTerms}
            onChange={(e) => setAcceptedGoogleTerms(e.target.checked)}
            className="terms-checkbox"
          />
          <span>
            I agree to the{" "}
            <Link to="/terms-and-conditions" target="_blank" className="terms-link">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy-policy" target="_blank" className="terms-link">
              Privacy Policy
            </Link>.
          </span>
        </label>
      </div>

      <div className="modal-actions" style={{ marginTop: "20px" }}>
        <button
          className="login-btn"
          onClick={handleGoogleCompleteSignup}
          disabled={loading || !acceptedGoogleTerms}
        >
          {loading ? "Processing..." : "Accept & Continue"}
        </button>
      </div>
    </div>
  </div>
)}


      {showAlertModal && (
        <div className="modal-overlay">
          <div className="modal-box">
            <h3>{isSuspended ? "Account Suspended" : "Alert"}</h3>
            <p>{isSuspended ? `Your account has been suspended. ` : alertMessage}</p>
            {suspendReason && (
              <p style={{ marginTop: "8px", fontWeight: "500" }}>
                <strong>Reason:</strong> {suspendReason}
              </p>
            )}
            <div className="modal-actions">
              <button
                className="btn-secondary"
                onClick={() => {
                  setShowAlertModal(false);
                  if (isSuspended) setIsSuspended(false);
                  localStorage.removeItem("isSuspended");
                }}
              >
                Close
              </button>

              {!isSuspended && alertMessage === "Email exists. Confirm account linking." && (
                <button className="btn-primary" onClick={handleAccountLinking} disabled={loading}>
                  Link Account
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {showToast && <div className="toast-notification">{toastMessage}</div>}

      {loading && (
        <div className="modal-overlay">
          <div className="modal-box" style={{ textAlign: "center" }}>
            <div className="loader"></div>
            <p>Processing, please wait...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default RegisterPage;
